/**
 * @lodex-pro/web-portal/routes/PortalHome — portal landing page (Task 15.4).
 *
 * Extracted from App.tsx (DEC-152) and extended with a "Quick tiles" grid that
 * links to the primary portal routes. Tiles disabled + tooltip when the operator's
 * plan does not include the feature (OPP PR 6: its features, as the API gates them;
 * OPP PR 13a removed the reports and webhooks placeholders, which locked by tier, and the
 * tier badge, until those ship as real plan features). Nav
 * sidebar (PortalShell) mirrors the same gating, but the tiles offer a discoverable
 * home surface.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { usePortalMe } from '../hooks/useOperatorPortal';
import { portalOrgName } from '../nav/org-name';
import { NOT_ON_PLAN, portalLacks } from '../nav/portal-features';

interface TileProps {
  readonly to: string;
  readonly title: string;
  readonly description: string;
  readonly allowed: boolean;
  /** Shown on a locked tile. */
  readonly lockedTitle?: string;
}

function Tile({
  to,
  title,
  description,
  allowed,
  lockedTitle = NOT_ON_PLAN,
}: TileProps): React.JSX.Element {
  const style: React.CSSProperties = {
    display: 'block',
    padding: '16px',
    border: '1px solid var(--color-border-secondary)',
    borderRadius: '10px',
    textDecoration: 'none',
    color: allowed ? 'inherit' : 'var(--color-text-tertiary)',
    opacity: allowed ? 1 : 0.6,
    cursor: allowed ? 'pointer' : 'not-allowed',
    background: 'var(--color-bg-tertiary)',
    minHeight: '80px',
  };
  const heading = (
    <>
      <div style={{ fontWeight: 600, marginBottom: '4px' }}>
        {title}
        {!allowed && <span style={{ marginLeft: '8px', fontSize: '11px' }}>🔒</span>}
      </div>
      <div style={{ fontSize: '13px', color: 'var(--color-text-tertiary)' }}>{description}</div>
    </>
  );
  if (allowed) {
    return (
      <Link to={to} style={style} aria-label={`${title} — ${description}`}>
        {heading}
      </Link>
    );
  }
  return (
    <div style={style} role="link" aria-disabled="true" title={lockedTitle}>
      {heading}
    </div>
  );
}

export function PortalHome(): React.JSX.Element {
  const { data, isLoading, isError } = usePortalMe();

  return (
    <main style={{ padding: '32px', maxWidth: '960px', margin: '0 auto' }}>
      <h1 style={{ marginBottom: '8px' }}>lodeX Pro Portal</h1>

      {isLoading && <p role="status">Loading…</p>}
      {isError && <p role="alert">Could not load account details.</p>}

      {data !== undefined && (
        <>
          <section aria-label="Account summary" style={{ marginTop: '16px' }}>
            <p style={{ fontSize: '18px', marginBottom: '8px' }}>
              Welcome, <strong>{data.fullName}</strong>
            </p>
            <p style={{ marginBottom: '16px' }}>
              <strong>Organisation:</strong> {portalOrgName(data)}
            </p>
            <p style={{ color: 'var(--color-text-tertiary)', fontSize: '13px' }}>
              Role: {data.roleCode} &mdash; {data.email}
            </p>
          </section>

          <section aria-label="Quick tiles" style={{ marginTop: '32px' }}>
            <h2 style={{ fontSize: '16px', marginBottom: '12px' }}>Quick access</h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: '12px',
              }}
            >
              <Tile
                to="/portal/tickets"
                title="Tickets"
                description="View your haul tickets"
                allowed={!portalLacks(data, 'OP_TICKETS')}
              />
              <Tile
                to="/portal/volumes"
                title="Volumes"
                description="BBL by site, tank, or month"
                allowed={!portalLacks(data, 'OP_VOLUMES')}
              />
              <Tile
                to="/portal/reports"
                title="Reports"
                description="A monthly statement as a PDF"
                allowed={!portalLacks(data, 'OP_TICKETS') && !portalLacks(data, 'OP_REPORTS')}
              />
              <Tile
                to="/portal/tanks"
                title="Tanks"
                description="Current levels and 90-day trends"
                allowed={!portalLacks(data, 'OP_TANK_LEVELS')}
              />
              <Tile
                to="/portal/api-keys"
                title="API access"
                description="Programmatic access to your data"
                allowed={!portalLacks(data, 'OP_API')}
              />
            </div>
          </section>
        </>
      )}
    </main>
  );
}
